import { batch } from 'react-redux';
import { toast } from 'react-toastify';

import { unset } from 'lodash';
import { useAppDispatch } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import {
  useDeleteMediaMutation,
  useFetchMediaMutation,
  useLazyFetchCollectionsQuery,
  useUpdateMediaMutation,
  useUploadMediaMutation,
} from '../api/media.api';
import { MediaAC, setAvailableCollections, setMediaCurrentPage } from '../store/media.slice';
import {
  ConvertMediaPayload,
  DeleteMediaRequest,
  FetchMediaPayload,
  GetCollectionsPayload,
  UpdateMediaPayload,
  UploadMediaPayload,
} from '../types/media.types';

interface UseMedia {
  uploadMediaHandler: (payload: UploadMediaPayload) => Promise<boolean>;
  deleteMediaHandler: (payload: DeleteMediaRequest[]) => Promise<boolean>;
  fetchMediaHandler: (payload: FetchMediaPayload) => Promise<boolean>;
  convertMediaToFile: (payload: ConvertMediaPayload) => Promise<File | null>;
  updateMediaHandler: (payload: UpdateMediaPayload) => Promise<boolean>;
  fetchCollectionsHandler: (payload: GetCollectionsPayload) => Promise<boolean>;
  clearMediaState: () => void;
}

export const useMedia = (): UseMedia => {
  const dispatch = useAppDispatch();

  const [fetchMedia] = useFetchMediaMutation();
  const [uploadMedia] = useUploadMediaMutation();
  const [deleteMedia] = useDeleteMediaMutation();
  const [updateMedia] = useUpdateMediaMutation();
  const [fetchCollections] = useLazyFetchCollectionsQuery();

  const fetchCollectionsHandler = async (payload: GetCollectionsPayload) => {
    const { data: response, error } = await fetchCollections(payload);
    const isError = error || !response?.data;
    if (!isError) {
      dispatch(setAvailableCollections(response.data));
    }

    return !isError;
  };

  const fetchMediaHandler = async (payload: FetchMediaPayload) => {
    dispatch(MediaAC.toggleLoading(true));
    const { data: response, error } = await fetchMedia(payload);
    dispatch(MediaAC.toggleLoading(false));
    const isError = error || !response?.data;
    if (isError) {
      toast.error(getApiErrorMessage(error) || 'Failed to get files!');
      return false;
    }

    if (payload.withAddItems) {
      dispatch(MediaAC.addItems(response.data));
    } else {
      dispatch(MediaAC.setItems(response.data));
    }

    dispatch(MediaAC.setPaginationMeta(response.meta));

    return !isError;
  };

  const updateMediaHandler = async (payload: UpdateMediaPayload) => {
    dispatch(MediaAC.toggleLoading(true));
    const { data, error } = await updateMedia(payload);
    dispatch(MediaAC.toggleLoading(false));
    if (error || !data) {
      toast.error(getApiErrorMessage(error) || 'Failed to update file!');
      return false;
    }
    return true;
  };

  const uploadMediaHandler = async (payload: UploadMediaPayload) => {
    dispatch(MediaAC.toggleLoading(true));
    const { collection, modelType, id } = payload;
    const files = payload.files;
    unset(payload, 'files');
    const { data, error } = await uploadMedia({
      params: { collection, modelType, id },
      body: { ...payload, 'files[]': files },
    });
    dispatch(MediaAC.toggleLoading(false));
    if (error || !data) {
      toast.error(getApiErrorMessage(error) || 'Failed to upload file!');
      return false;
    }
    return true;
  };

  const deleteMediaHandler = async (payload: DeleteMediaRequest[]) => {
    for (const image of payload) {
      const { data, error } = await deleteMedia(image);
      if (error || !data) {
        toast.error(getApiErrorMessage(error) || 'Failed to delete file!');
        return false;
      }
    }
    return true;
  };

  const convertMediaToFile = async (payload: ConvertMediaPayload) => {
    try {
      const response = await fetch(payload.url);
      const blob = await response.blob();
      const fileName = `${payload.name}.${payload.extension}`;

      if (payload.withDownload) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('style', 'display: none');
        link.setAttribute('download', fileName);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      return new File([blob], fileName, {
        type: payload.mimeType,
        lastModified: Date.now(),
      });
    } catch (error) {
      return null;
    }
  };

  const clearMediaState = () => {
    batch(() => {
      dispatch(MediaAC.setItems([]));
      dispatch(MediaAC.setPaginationMeta(null));
      dispatch(MediaAC.clearFilters());
      dispatch(MediaAC.toggleLoading(false));
      dispatch(setMediaCurrentPage(1));
    });
  };

  return {
    deleteMediaHandler,
    uploadMediaHandler,
    fetchMediaHandler,
    convertMediaToFile,
    updateMediaHandler,
    clearMediaState,
    fetchCollectionsHandler,
  };
};
